import React, { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Loader2, ShieldAlert } from "lucide-react";
import { eyePush, eyeFaces, eyeIntruder, eyeFrame, eyeGesture, eyeVitals, eyeSetup, eyeAddFace } from "../lib/api";
import { loadGestures, readGesture, makeHold, sampleRoi, makeVitals } from "./eyeSentry";
import { EyeFaces, captureFace } from "./EyeFaces";
import { EyeVisits } from "./EyeVisits";
import { identify, makeLiveness } from "./eyeMath";
import { CameraSource, DEFAULT_BRIDGE } from "./EyeCameraSource";
import { EyeBridgeBadge } from "./EyeBridgeBadge";
import { loadPersons, findPeople, zoomFaces, inBox, bodySig, bodySim, loadDayBodies, saveDayBody, BODY_MATCH } from "./eyeZoom";

const CDN = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.15";
const loadFaceApi = () => new Function("u", "return import(u)")(`${CDN}/dist/face-api.esm.js`);
const STRANGER_TICKS = 4;
const STABLE = 3; // a name must win 3 of the last 4 frames before Nexus treats it as that person
const CLEAR_PX = 96;
const PTZ_HELPER = "http://localhost:1985"; // room camera: a face smaller than this (real pixels) is too blurry to call anyone a stranger

const IntruderWall = ({ videoRef }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!ref.current || !v) return;
    if (v.srcObject) ref.current.srcObject = v.srcObject;
    else if (v.src) { ref.current.crossOrigin = "anonymous"; ref.current.src = v.src; }
    ref.current.play().catch(() => {});
  }, [videoRef]);
  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center gap-4 nx-intruder" data-testid="nexus-eye-intruder-wall">
      <video ref={ref} playsInline muted className="absolute inset-0 w-full h-full object-cover -scale-x-100 opacity-70 nx-intruder-feed" />
      <div className="absolute inset-0 border-[16px] border-rose-600 animate-pulse pointer-events-none" />
      <div className="relative text-center px-6">
        <ShieldAlert className="w-24 h-24 text-rose-500 mx-auto animate-pulse" />
        <p className="nx-orbitron text-[clamp(2.5rem,7vw,6rem)] font-black tracking-widest text-rose-500 nx-glitch">INTRUDER</p>
        <p className="text-[clamp(1rem,2.4vw,2rem)] font-bold text-rose-100">YOU ARE BEING RECORDED. THE OWNER HAS BEEN ALERTED.</p>
        <p className="nx-mono text-rose-300/80 mt-2 text-sm">FACE CAPTURED · {new Date().toLocaleTimeString("en-US", { timeZone: "America/Chicago" })} CT</p>
      </div>
    </div>
  );
};

export default function NexusEye({ session }) {
  const videoRef = useRef(null);
  const setupRef = useRef(false);
  const teachRef = useRef({ last: null, report: null });
  const [tvSetup, setTvSetup] = useState(false);
  const apiRef = useRef(null);
  const facesRef = useRef([]);
  const stopRef = useRef(false);
  const strangeRef = useRef(0);
  const histRef = useRef([]);
  const liveRef = useRef(makeLiveness());
  const unkDescRef = useRef([]);
  const lastFrameRef = useRef(0);
  const ownerRef = useRef({ until: 0, name: "", pts: null });
  const gestRef = useRef(null);
  const holdRef = useRef(makeHold(500));
  const vitRef = useRef(makeVitals(30));
  const roiCanvas = useRef(null);
  const lastVitRef = useRef(0);
  const personRef = useRef(null);
  const peopleRef = useRef([]);
  const bodyRef = useRef(loadDayBodies());
  const sigCanvas = useRef(null);
  const moveRef = useRef(0);
  const errRef = useRef(0);
  const [zoom, setZoom] = useState({ s: 1, ox: 50, oy: 50 });
  const [sentry, setSentry] = useState("");
  const [faces, setFaces] = useState([]);
  const [state, setState] = useState("idle");
  const [info, setInfo] = useState("");
  const [tone, setTone] = useState("idle");
  const [intruder, setIntruder] = useState(false);
  const qs = new URLSearchParams(window.location.search);
  const [source, setSource] = useState(() => qs.get("source") || localStorage.getItem("nx_eye_source") || "webcam");
  const [bridge, setBridge] = useState(() => localStorage.getItem("nx_eye_bridge") || DEFAULT_BRIDGE);
  useEffect(() => { localStorage.setItem("nx_eye_source", source); localStorage.setItem("nx_eye_bridge", bridge); }, [source, bridge]);
  facesRef.current = faces;

  useEffect(() => { eyeFaces(session.user_id).then((d) => setFaces(d.faces || [])).catch(() => {}); }, [session.user_id]);
  useEffect(() => () => { stopRef.current = true; }, []);

  const detect = async () => {
    const fa = apiRef.current;
    const wide = source === "tapo"; // room camera: faces are small, so look at a bigger frame
    const full = await fa.detectAllFaces(videoRef.current, new fa.TinyFaceDetectorOptions({ inputSize: wide ? 704 : 320, scoreThreshold: wide ? 0.35 : 0.5 })).withFaceLandmarks().withFaceDescriptors();
    if (!wide || !personRef.current) { peopleRef.current = []; return full; }
    // far away: find each person's body, then digitally zoom on the head from the full-quality frame
    try {
      peopleRef.current = findPeople(personRef.current, videoRef.current);
      const z = await zoomFaces(fa, videoRef.current, peopleRef.current, 704);
      return [...full.filter((f) => !z.some((q) => inBox(f, q.region))), ...z];
    } catch (e) { peopleRef.current = []; return full; } // body finder failing must never block normal face recognition
  };
  const sample = async () => {  // enrol the nearest (largest) face, even if others are in the room
    const r = await detect();
    const big = [...r].sort((a, b) => b.detection.box.width - a.detection.box.width)[0];
    return big && (r.length === 1 || big.detection.box.width > 1.3 * r.filter((x) => x !== big).reduce((m, x) => Math.max(m, x.detection.box.width), 0))
      ? { descriptor: big.descriptor, landmarks: big.landmarks } : null;
  };
  // Remote "Teach Nexus my face" from the TV A setup screen: guided capture here, prompts mirrored on the TV
  const teachFromTv = async (t) => {
    teachRef.current.last = t.id;
    const say = (rep) => { teachRef.current.report = { name: t.name, ...rep }; };
    say({ state: "started" });
    const descs = await captureFace(sample, (st) => say({ state: "capturing", ...st }));
    if (descs.length < 9) return say({ state: "failed", label: `Only ${descs.length} good samples — better light, one face, follow each prompt` });
    try {
      const r = await eyeAddFace(session.user_id, t.name, t.role || "owner", descs);
      setFaces(r.faces || []); facesRef.current = r.faces || [];
      say({ state: "done", label: `Nexus now knows ${t.name} (${descs.length} samples)` });
    } catch (e) { say({ state: "failed", label: "Could not save the face" }); }
  };
  const snapshot = (q = 0.75, w = 0) => {
    const v = videoRef.current; const c = document.createElement("canvas");
    const s = w ? w / v.videoWidth : 1;
    c.width = v.videoWidth * s; c.height = v.videoHeight * s; c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", q);
  };

  // Owner's body for the day: learned only from a person whose face is a confirmed match; used when the face isn't visible
  const bodyMatch = (ids, stableNames) => {
    const v = videoRef.current, found = {};
    sigCanvas.current = sigCanvas.current || document.createElement("canvas");
    peopleRef.current.forEach((b) => {
      const inside = ids.filter((x) => x.m.kind !== "far" && inBox(x.r, b));
      const sig = bodySig(v, b, sigCanvas.current);
      const me = inside.find((x) => x.m.kind === "known" && stableNames.includes(x.m.name));
      if (me) return saveDayBody(bodyRef.current, me.m.name, sig);
      if (inside.length) return; // a face is visible here but isn't confirmed: never stand in for a face check
      Object.entries(bodyRef.current).forEach(([name, s]) => {
        const sim = bodySim(s, sig);
        if (sim >= BODY_MATCH && sim > (found[name] || 0)) found[name] = sim;
      });
    });
    return found;
  };

  // Room camera: smooth digital zoom on the person + a small pan/tilt step (office PC helper) when they drift to the edge
  const aim = (b) => {
    const v = videoRef.current;
    if (!b || !v?.videoWidth) { setZoom((z) => (z.s === 1 ? z : { s: 1, ox: 50, oy: 50 })); return; }
    const cx = (b.originX + b.width / 2) / v.videoWidth, cy = (b.originY + b.height * 0.3) / v.videoHeight;
    const s = Math.round(Math.min(2.2, Math.max(1, 0.55 / (b.height / v.videoHeight))) * 10) / 10;
    setZoom((z) => (Math.abs(z.s - s) < 0.2 && Math.abs(z.ox - cx * 100) < 8 && Math.abs(z.oy - cy * 100) < 8 ? z : { s, ox: Math.round(cx * 100), oy: Math.round(cy * 100) }));
    if (Date.now() - moveRef.current < 3000) return;
    const px = cx > 0.7 ? 0.25 : cx < 0.3 ? -0.25 : 0, py = b.originY / v.videoHeight < 0.03 ? 0.2 : cy > 0.8 ? -0.2 : 0;
    if (!px && !py) return;
    moveRef.current = Date.now();
    fetch(`${PTZ_HELPER}/move?x=${px}&y=${py}&ms=300`, { mode: "no-cors" }).catch(() => {});
  };

  const tick = async () => {
    if (stopRef.current) return;
    const v = videoRef.current;
    try {
      const slow = setTimeout(() => setInfo("Slow frame — still looking…"), 8000); // never start a 2nd detect while one is running
      const res = await detect().finally(() => clearTimeout(slow));
      const big = [...res].sort((a, b) => b.detection.box.width - a.detection.box.width)[0];
      const ids = res.map((r) => {
        const m = identify(r.descriptor, facesRef.current);
        // far / zoomed faces are fuzzy: they may confirm a known person, but are never "unknown" (no false intruder alarms)
        return { r, m: source === "tapo" && m.kind === "unknown" && r.detection.box.width < CLEAR_PX ? { kind: "far", d: m.d } : m };
      });
      histRef.current = [...histRef.current.slice(-3), ids.filter((x) => x.m.kind === "known").map((x) => x.m.name)];
      const stableNames = [...new Set(histRef.current.flat())].filter((n) => histRef.current.filter((h) => h.includes(n)).length >= STABLE);
      const conf = {}; ids.forEach((x) => { if (x.m.kind === "known") conf[x.m.name] = Math.max(conf[x.m.name] || 0, x.m.conf); });
      const bigId = ids.find((x) => x.r === big)?.m;
      const isLive = big ? liveRef.current(big.landmarks) : false;
      const live = isLive && bigId?.kind === "known" && stableNames.includes(bigId.name) ? [bigId.name] : [];
      const owners = facesRef.current.filter((f) => f.role === "owner").map((f) => f.name);
      const ownerName = stableNames.find((n) => owners.includes(n));
      if (ownerName) ownerRef.current = { until: Date.now() + 2500, name: ownerName, pts: bigId?.name === ownerName ? big.landmarks.positions : ownerRef.current.pts };
      const bodies = source === "tapo" ? bodyMatch(ids, stableNames) : {};
      const bodyNames = Object.keys(bodies).filter((n) => !stableNames.includes(n));
      if (source === "tapo") aim(peopleRef.current.find((b) => big && inBox(big, b)) || [...peopleRef.current].sort((a, b) => b.height - a.height)[0]);
      const unknown = ids.filter((x) => x.m.kind === "unknown").length;
      const maybe = ids.filter((x) => x.m.kind === "maybe");
      let x = 0, y = 0, w = 0;
      if (big) {
        const bb = big.detection.box;
        x = -((bb.x + bb.width / 2) / v.videoWidth * 2 - 1);
        y = (bb.y + bb.height / 2) / v.videoHeight * 2 - 1;
        w = bb.width / v.videoWidth;
      }
      const stranger = unknown > 0 && stableNames.length === 0 && facesRef.current.length > 0;
      strangeRef.current = stranger ? strangeRef.current + 1 : 0;
      if (stranger) {
        const u = ids.find((q) => q.m.kind === "unknown");
        if (u) unkDescRef.current = [...unkDescRef.current.slice(-9), Array.from(u.r.descriptor)];
      } else if (!unknown) unkDescRef.current = [];
      if (strangeRef.current === STRANGER_TICKS) { setIntruder(true); eyeIntruder(session.user_id, snapshot(), unkDescRef.current).catch(() => {}); }
      if (strangeRef.current >= STRANGER_TICKS && Date.now() - lastFrameRef.current > 1500) { lastFrameRef.current = Date.now(); eyeFrame(session.user_id, snapshot(0.6, 480)).catch(() => {}); }
      if (stableNames.length || !res.length) setIntruder(false);
      setTone(stableNames.length || bodyNames.length ? "known" : stranger ? "stranger" : "idle");
      setInfo(!res.length && bodyNames.length ? `Hello ${bodyNames.map((n) => `${n} (body match ${Math.round(bodies[n] * 100)}%)`).join(", ")} · face the camera once for a face check`
        : !res.length ? (peopleRef.current.length ? `${peopleRef.current.length} person(s) in view — zooming in on the face…` : "No one in view")
        : stableNames.length ? `Hello ${stableNames.map((n) => `${n} (${Math.round((conf[n] || 0) * 100)}%)`).join(", ")}${live.length ? " · live ✓" : " · blink or turn your head"}`
        : maybe.length ? `Maybe ${maybe[0].m.name}? Come closer…` : stranger ? "Unknown person!" : ids.every((q) => q.m.kind === "far") ? "Someone far away — zooming in, face too small to check yet" : `${res.length} face(s)`);
      const report = teachRef.current.report; teachRef.current.report = report && report.state === "capturing" ? report : null;
      const vw = videoRef.current?.videoWidth || 1, vh = videoRef.current?.videoHeight || 1;
      const mesh = setupRef.current && big ? big.landmarks.positions.map((p) => [p.x / vw, p.y / vh]) : undefined;
      eyePush(session.user_id, { present: !!big || bodyNames.length > 0, x, y, w, faces: res.length, who: [...stableNames, ...bodyNames], unknown, conf, live, ...(report ? { teach: report } : {}), ...(mesh ? { mesh } : {}) })
        .then((r) => { setupRef.current = !!r.setup; setTvSetup(!!r.setup); if (r.teach && r.teach.id !== teachRef.current.last) teachFromTv(r.teach); }).catch(() => {});
      if (setupRef.current && !stranger) eyeFrame(session.user_id, snapshot(0.7, 1280)).catch(() => {});
      errRef.current = 0;
    } catch (e) {
      errRef.current += 1;
      setInfo(`Eye hiccup (${String(e?.message || e).slice(0, 80)}) — retrying…`);
      if (errRef.current === 5) personRef.current = null; // body finder keeps failing: plain face recognition only
    }
    setTimeout(tick, 600);
  };

  // Owner-gated fast loop (~14 fps): gestures (work on IR too) + rPPG vitals (daylight only)
  const fast = () => {
    if (stopRef.current) return;
    const v = videoRef.current, now = performance.now(), o = ownerRef.current;
    if (o.until > Date.now() && v.videoWidth) {
      try {
        const g = readGesture(gestRef.current.recognizeForVideo(v, now));
        const fire = holdRef.current(g, now);
        if (fire) eyeGesture(session.user_id, fire, o.name).then((r) => r.ok && setSentry(`✋ ${fire} → ${r.action}`)).catch(() => {});
        if (o.pts) {
          roiCanvas.current = roiCanvas.current || document.createElement("canvas");
          const smp = sampleRoi(v, o.pts, roiCanvas.current);
          if (smp && (smp.mono || smp.dark)) {
            vitRef.current.reset();
            if (Date.now() - lastVitRef.current > 15000) { lastVitRef.current = Date.now(); eyeVitals(session.user_id, { status: "low_light" }).catch(() => {}); }
            setSentry("Gestures armed · vitals paused — low light");
          } else if (smp) {
            vitRef.current.push(Date.now(), smp);
            if (Date.now() - lastVitRef.current > 5000) {
              lastVitRef.current = Date.now();
              const r = vitRef.current.compute();
              if (r) { eyeVitals(session.user_id, { ...r, status: "ok" }).catch(() => {}); setSentry(`Gestures armed · ♥ ${r.hr} bpm · ${r.br} breaths/min (wellness estimate, not diagnostic)`); }
              else setSentry(`Gestures armed · reading vitals… ${Math.round(vitRef.current.span())}/30s, hold still`);
            }
          }
        }
      } catch (e) {}
    } else { holdRef.current(null, now); vitRef.current.reset(); }
    setTimeout(fast, 70);
  };

  const start = async () => {
    setState("loading"); setInfo("Loading face models…");
    try {
      const v = videoRef.current;
      const webcam = async () => {
        v.removeAttribute("src"); v.srcObject = null;
        v.srcObject = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640, height: 480 }, audio: false })
          .catch(() => { throw new Error("Camera blocked. Click the camera icon in the address bar → Allow, then try again."); });
        await v.play();
      };
      if (source === "tapo") {
        v.srcObject = null; v.crossOrigin = "anonymous"; v.src = bridge.trim();
        setInfo("Connecting to the Tapo C210…");
        const ok = await Promise.race([v.play().then(() => true).catch(() => false), new Promise((r) => setTimeout(() => r(false), 8000))]);
        if (!ok) {
          // Tapo bridge not running on this PC: keep Nexus useful with the laptop webcam instead of failing
          setSource("webcam");
          setSentry("Tapo bridge isn't running on this PC, so I'm using the laptop webcam. Pick Tapo C210 → Windows PC to install the bridge.");
          await webcam();
        }
      } else {
        await webcam();
      }
      setInfo("Loading face models…");
      const fa = apiRef.current || (await loadFaceApi());
      if (!apiRef.current) {
        const ok = await fa.tf.setBackend("webgl").catch(() => false);
        if (!ok) await fa.tf.setBackend("cpu");
        await fa.tf.ready();
      }
      await Promise.all([fa.nets.tinyFaceDetector.loadFromUri(`${CDN}/model`), fa.nets.faceLandmark68Net.loadFromUri(`${CDN}/model`), fa.nets.faceRecognitionNet.loadFromUri(`${CDN}/model`)]);
      if (source === "tapo" && !personRef.current) {
        setInfo("Loading body finder for far-away faces…");
        personRef.current = await loadPersons().catch(() => null);
      }
      apiRef.current = fa; stopRef.current = false; setState("on"); setInfo("Looking…"); tick();
      loadGestures().then((g) => { gestRef.current = g; fast(); }).catch((e) => setSentry(`Gestures unavailable: ${String(e?.message || e).slice(0, 80)}`));
    } catch (e) { setState("error"); setInfo(String(e?.message || e).slice(0, 140)); }
  };
  // ?auto=1 (from the one-click launcher): open the eye automatically once faces are loaded
  const autoRef = useRef(qs.get("auto") === "1");
  useEffect(() => {
    if (!autoRef.current) return undefined;
    const t = setTimeout(() => { if (autoRef.current) { autoRef.current = false; start(); } }, 1500);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stop = () => {
    stopRef.current = true;
    const v = videoRef.current;
    const s = v?.srcObject; if (s) s.getTracks().forEach((t) => t.stop());
    if (v?.src) { v.pause(); v.removeAttribute("src"); v.load(); }
    setState("idle"); setInfo(""); setTone("idle"); setIntruder(false);
  };

  const ring = tone === "known" ? "border-emerald-400" : tone === "stranger" ? "border-rose-500" : "border-cyan-300/20";
  return (
    <div className="min-h-screen bg-[#030712] text-cyan-50 flex flex-col items-center justify-center gap-5 p-6" data-testid="nexus-eye-page">
      {intruder && <IntruderWall videoRef={videoRef} />}
      <h1 className="text-3xl font-black tracking-widest text-[#00F0FF]">NEXUS EYE</h1>
      <EyeBridgeBadge bridge={bridge} streaming={state === "on" && source === "tapo"} />
      <button onClick={() => eyeSetup(session.user_id, !tvSetup).then((r) => { setupRef.current = r.setup; setTvSetup(r.setup); }).catch(() => {})}
        className={`rounded-full px-5 py-2 text-xs font-bold border ${tvSetup ? "border-emerald-400 text-emerald-200 bg-emerald-500/10" : "border-cyan-300/40 text-cyan-200 bg-white/5"}`}
        data-testid="eye-tv-setup-toggle">{tvSetup ? "● Showing on TV A — stop" : "Show camera on TV A for setup"}</button>
      <p className="text-cyan-200/60 text-sm text-center max-w-md">
        Point this laptop's camera at the door. Faces are recognised on this laptop only. Nexus gets names and positions, and photos only when a stranger is detected.
      </p>
      <CameraSource source={source} setSource={setSource} bridge={bridge} setBridge={setBridge} locked={state !== "idle" && state !== "error"} />
      <div className={`w-80 h-60 rounded-2xl border-4 ${ring} overflow-hidden transition-colors`}>
        <video ref={videoRef} playsInline muted className={`w-full h-full object-cover ${source === "webcam" ? "-scale-x-100" : ""}`} data-testid="nexus-eye-video"
          style={source === "tapo" ? { transform: `scale(${zoom.s})`, transformOrigin: `${zoom.ox}% ${zoom.oy}%`, transition: "transform 1.5s ease, transform-origin 1.5s ease" } : undefined} />
      </div>
      <p className={`text-base font-bold min-h-[1.5rem] ${tone === "stranger" ? "text-rose-400" : tone === "known" ? "text-emerald-300" : "text-cyan-200/80"}`} data-testid="nexus-eye-status">{info}</p>
      {sentry && <p className="text-xs nx-mono text-cyan-200/70 text-center max-w-md" data-testid="nexus-eye-sentry">{sentry}</p>}
      {state === "on" ? (
        <button onClick={stop} data-testid="nexus-eye-stop" className="flex items-center gap-2 bg-rose-500/20 text-rose-200 border border-rose-400/40 rounded-full px-6 py-3 font-bold"><EyeOff className="w-5 h-5" /> Close Nexus's eye</button>
      ) : (
        <button onClick={start} disabled={state === "loading"} data-testid="nexus-eye-start" className="flex items-center gap-2 bg-[#00F0FF] text-[#030712] rounded-full px-6 py-3 font-bold disabled:opacity-60">
          {state === "loading" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Eye className="w-5 h-5" />} Open Nexus's eye
        </button>
      )}
      <EyeFaces userId={session.user_id} faces={faces} setFaces={setFaces} sample={sample} ready={state === "on"} />
      <EyeVisits userId={session.user_id} />
      <p className="text-[11px] text-cyan-200/40 text-center max-w-sm">Keep this tab open and the laptop plugged in, with sleep turned off.</p>
    </div>
  );
}
